import {
  AlertCircle,
  ArrowLeft,
  BarChart3,
  Bot,
  BrainCircuit,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Clock,
  Code2,
  Copy,
  Database,
  Download,
  File,
  FileOutput,
  FileText,
  GitBranch,
  Globe,
  Layers,
  ListTodo,
  Loader2,
  type LucideIcon,
  Maximize2,
  MessageSquare,
  Minimize2,
  Pause,
  Play,
  Search,
  Send,
  Share2,
  Square,
  User,
  Wrench,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SourcesPanel } from "@/components/chat/SourcesPanel";
import { EntityShareDialog, TagPopover, useToast } from "@/components/shared";
import { Markdown } from "@/components/shared/Markdown";
import { VoiceInputButton } from "@/components/shared/VoiceInputButton";
import { AskUserCard } from "@/components/tasks/AskUserCard";
import { DelegatedStepDetail } from "@/components/tasks/DelegatedStepDetail";
import { useOperations } from "@/context/OperationContext";
import { type OperationEvent, useOperationStream } from "@/hooks/useOperationStream";
import { taskService } from "@/lib/services";
import type {
  AskUserQuestion,
  ChildTaskSummary,
  SourceMetadata,
  SSEEvent,
  Task,
  TaskArtifact,
  TaskStatus,
  TaskStep,
  TaskStepStatus,
  ToolEndEvent,
  ToolStartEvent,
} from "@/types";

// -- helpers --

const statusConfig: Record<TaskStatus, { bg: string; color: string; icon: LucideIcon; label: string }> = {
  pending: { bg: "bg-surface-tertiary/20", color: "text-content-tertiary", icon: Clock, label: "Pending" },
  planning: { bg: "bg-blue-500/20", color: "text-blue-400", icon: BrainCircuit, label: "Planning" },
  running: { bg: "bg-blue-500/20", color: "text-blue-400", icon: Loader2, label: "Running" },
  paused: { bg: "bg-yellow-500/20", color: "text-yellow-400", icon: Pause, label: "Paused" },
  waiting_for_input: {
    bg: "bg-yellow-500/20",
    color: "text-yellow-400",
    icon: AlertCircle,
    label: "Waiting for Input",
  },
  completed: { bg: "bg-green-500/20", color: "text-green-400", icon: CheckCircle2, label: "Completed" },
  failed: { bg: "bg-red-500/20", color: "text-red-400", icon: AlertCircle, label: "Failed" },
  cancelled: { bg: "bg-surface-tertiary/20", color: "text-content-tertiary", icon: XCircle, label: "Cancelled" },
};

function StepStatusIcon({ status }: Readonly<{ status: TaskStepStatus }>) {
  switch (status) {
    case "running":
      return <Loader2 className="h-4 w-4 text-blue-400 animate-spin" />;
    case "completed":
      return <CheckCircle2 className="h-4 w-4 text-green-400" />;
    case "failed":
      return <XCircle className="h-4 w-4 text-red-400" />;
    case "skipped":
      return <Clock className="h-4 w-4 text-content-quaternary" />;
    default:
      return <Clock className="h-4 w-4 text-content-quaternary" />;
  }
}

function stepBorderClass(status: TaskStepStatus): string {
  switch (status) {
    case "running":
      return "bg-blue-500/10 border-blue-500/30";
    case "completed":
      return "bg-green-500/10 border-green-500/30";
    case "failed":
      return "bg-red-500/10 border-red-500/30";
    default:
      return "bg-surface-secondary/50 border-border-secondary";
  }
}

function ArtifactTypeIcon({ type }: Readonly<{ type: string }>) {
  switch (type) {
    case "document":
      return <FileText className="h-5 w-5 text-blue-400" />;
    case "report":
      return <FileOutput className="h-5 w-5 text-green-400" />;
    case "analysis":
      return <BarChart3 className="h-5 w-5 text-purple-400" />;
    case "code":
      return <Code2 className="h-5 w-5 text-yellow-400" />;
    case "data":
      return <Database className="h-5 w-5 text-cyan-400" />;
    case "plan":
      return <ClipboardList className="h-5 w-5 text-orange-400" />;
    case "summary":
      return <FileText className="h-5 w-5 text-emerald-400" />;
    default:
      return <File className="h-5 w-5 text-content-tertiary" />;
  }
}

const artifactTypeLabels: Record<string, string> = {
  document: "Document",
  report: "Report",
  analysis: "Analysis",
  code: "Code",
  data: "Data",
  plan: "Plan",
  summary: "Summary",
  other: "Other",
};

/** Map tool names to friendly labels + icons for the follow-up UI. */
const FOLLOWUP_TOOL_LABELS: Record<string, { label: string; icon: LucideIcon }> = {
  dynamic_session: { label: "Running code...", icon: Code2 },
  web_search: { label: "Searching the web...", icon: Globe },
  file_search: { label: "Searching files...", icon: Search },
  create_artifact: { label: "Creating artifact...", icon: FileOutput },
  fetch_sharepoint_document: { label: "Fetching document...", icon: FileText },
};

function getFollowUpToolLabel(name: string): { label: string; icon: LucideIcon } {
  return FOLLOWUP_TOOL_LABELS[name] ?? { label: `Running ${name}...`, icon: Wrench };
}

/**
 * Groups steps into execution "waves" based on their `dependsOn` arrays.
 * Steps with no dependencies (or whose deps are all in earlier waves) form
 * concurrent groups. Falls back to one-step-per-wave if no step has deps.
 */
interface Wave<T> {
  /** Step indices (stepOrder values) in this wave */
  steps: T[];
  /** true when wave has >1 step (parallel execution) */
  isParallel: boolean;
}

function buildWaves<T extends { stepOrder: number; dependsOn?: number[] }>(steps: T[]): Wave<T>[] {
  // If no step has dependsOn, return sequential waves (one step per wave)
  const hasDeps = steps.some((s) => s.dependsOn && s.dependsOn.length > 0);
  if (!hasDeps) {
    return steps.map((s) => ({ steps: [s], isParallel: false }));
  }

  // Kahn's algorithm — group by topological level
  const byOrder = new Map(steps.map((s) => [s.stepOrder, s]));
  const completed = new Set<number>();
  const remaining = new Set(steps.map((s) => s.stepOrder));
  const waves: Wave<T>[] = [];

  while (remaining.size > 0) {
    const ready: T[] = [];
    for (const order of remaining) {
      const step = byOrder.get(order)!;
      const deps = step.dependsOn ?? [];
      if (deps.every((d) => completed.has(d) || !remaining.has(d))) {
        ready.push(step);
      }
    }
    if (ready.length === 0) {
      // Cycle or bad deps — push remaining as a single wave to avoid infinite loop
      const rest = [...remaining].map((o) => byOrder.get(o)!);
      waves.push({ steps: rest, isParallel: rest.length > 1 });
      break;
    }
    // Sort by stepOrder for stable display
    ready.sort((a, b) => a.stepOrder - b.stepOrder);
    waves.push({ steps: ready, isParallel: ready.length > 1 });
    for (const s of ready) {
      completed.add(s.stepOrder);
      remaining.delete(s.stepOrder);
    }
  }

  return waves;
}

// -- types for local streaming state --

interface StreamStep {
  stepOrder: number;
  title: string;
  description?: string;
  stepType: string;
  status: TaskStepStatus;
  output: string;
  summary?: string;
  tokensUsed: number;
  errorMessage?: string;
  sources?: SourceMetadata[];
  isDelegated?: boolean;
  delegatedTaskUuid?: string;
  dependsOn?: number[];
}

interface PlanStep {
  title: string;
  description: string;
  step_type: string;
  depends_on?: number[];
  is_delegated?: boolean;
}

// -- component --

export function TaskDetail() {
  // NOSONAR -- page component orchestrating task execution with complex state management
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { trackOperation, findByEntityId } = useOperations();

  // Data
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

  // Streaming execution state
  const [executing, setExecuting] = useState(false);
  const [liveStatus, setLiveStatus] = useState<TaskStatus | null>(null);
  const [planSteps, setPlanSteps] = useState<PlanStep[]>([]);
  const [streamSteps, setStreamSteps] = useState<StreamStep[]>([]);
  const [finalOutput, setFinalOutput] = useState<string | null>(null);
  const [totalTokens, setTotalTokens] = useState(0);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());

  // Input
  const [submittingInput, setSubmittingInput] = useState(false);
  const [askUserQuestion, setAskUserQuestion] = useState<string | null>(null);
  const [askUserOptions, setAskUserOptions] = useState<string[] | null>(null);
  const [askUserQuestions, setAskUserQuestions] = useState<AskUserQuestion[] | null>(null);

  // Cancel
  const [cancelling, setCancelling] = useState(false);

  // Synthesis
  const [synthesizing, setSynthesizing] = useState(false);

  // Continuation tracking
  const [continuationStartOrder, setContinuationStartOrder] = useState<number | null>(null);

  // Follow-up conversation
  const [followUpMessages, setFollowUpMessages] = useState<
    { role: "user" | "assistant"; content: string; sources?: SourceMetadata[] }[]
  >([]);
  const [followUpInput, setFollowUpInput] = useState("");
  const [followUpStreaming, setFollowUpStreaming] = useState(false);
  const [followUpStreamingContent, setFollowUpStreamingContent] = useState("");
  const [followUpActiveTool, setFollowUpActiveTool] = useState<string | null>(null);
  const followUpSourcesRef = useRef<SourceMetadata[]>([]);

  // Artifacts
  const [expandedArtifacts, setExpandedArtifacts] = useState<Set<string>>(new Set());
  const [copiedArtifact, setCopiedArtifact] = useState<string | null>(null);

  // Steps showing full output instead of summary
  const [fullOutputSteps, setFullOutputSteps] = useState<Set<number>>(new Set());

  const bottomRef = useRef<HTMLDivElement>(null);

  // Keep a ref of streamSteps in sync so event handlers can read current state
  const streamStepsRef = useRef<StreamStep[]>([]);
  streamStepsRef.current = streamSteps;

  // Keep a ref of task in sync so event handlers can backfill steps
  const taskRef = useRef<Task | null>(null);
  taskRef.current = task;

  // Throttle step content updates (accumulate chunks, flush every 50ms)
  const STEP_CONTENT_THROTTLE_MS = 50;
  const pendingChunksRef = useRef<Map<number, string>>(new Map());
  const lastStepFlushRef = useRef(0);

  // Load task
  useEffect(() => {
    if (taskId) {
      loadTask();
    }
  }, [taskId]);

  // Auto-scroll during execution
  useEffect(() => {
    if (executing || followUpStreaming) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [streamSteps, executing, followUpMessages, followUpStreamingContent, followUpStreaming]);

  // Auto-execute when arriving with ?autoStart=true (after task creation)
  const autoStartTriggered = useRef(false);
  useEffect(() => {
    if (
      !autoStartTriggered.current &&
      searchParams.get("autoStart") === "true" &&
      task?.status === "pending" &&
      !executing &&
      !loading
    ) {
      autoStartTriggered.current = true;
      // Clear the query param from URL without navigating
      setSearchParams({}, { replace: true });
      handleExecute();
    }
  }, [task, loading, executing, searchParams]);

  const loadTask = async () => {
    if (!taskId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await taskService.get(taskId);
      setTask(response.data);
      // Restore ask_user question and options from task context
      if (response.data.status === "waiting_for_input") {
        const ctx = response.data.context;
        if (ctx?.ask_user_question) {
          setAskUserQuestion(ctx.ask_user_question as string);
        }
        if (Array.isArray(ctx?.ask_user_options)) {
          setAskUserOptions(ctx.ask_user_options as string[]);
        }
        if (Array.isArray(ctx?.ask_user_questions)) {
          setAskUserQuestions(ctx.ask_user_questions as AskUserQuestion[]);
        }
      }
      // Restore follow-up conversation from task context
      const saved = response.data.context?.followup_messages;
      if (Array.isArray(saved) && saved.length > 0) {
        setFollowUpMessages(
          saved.map((m: { role: string; content: string }) => ({
            role: m.role as "user" | "assistant",
            content: m.content,
          })),
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load task");
      console.error("Error loading task:", err);
    } finally {
      setLoading(false);
    }
  };

  // Operation Manager stream
  const [operationId, setOperationId] = useState<string | null>(null);

  // ── Event handler helpers for operation stream ─────────────────────────

  const handleStepStart = useCallback(
    // NOSONAR -- step backfill logic requires nested conditionals
    (data: {
      step_order: number;
      title: string;
      description?: string;
      step_type: string;
      is_delegated?: boolean;
      depends_on?: number[];
    }) => {
      setStreamSteps((prev) => {
        // If streamSteps is empty but the task already has loaded steps,
        // backfill them so previously completed steps remain visible.
        let base = prev;
        if (base.length === 0 && taskRef.current?.steps && taskRef.current.steps.length > 0) {
          base = taskRef.current.steps.map((s) => ({
            stepOrder: s.stepOrder,
            title: s.title,
            description: s.description,
            stepType: s.stepType,
            status: s.status as TaskStepStatus,
            output: typeof s.output === "string" ? s.output : "",
            summary: s.summary,
            tokensUsed: s.tokensUsed,
            errorMessage: s.errorMessage,
            sources: s.sources,
            isDelegated: !!s.delegatedTaskUuid,
            delegatedTaskUuid: s.delegatedTaskUuid,
            dependsOn: s.dependsOn,
          }));
        }

        const exists = base.some((s) => s.stepOrder === data.step_order);
        if (exists) {
          return base.map((s) =>
            s.stepOrder === data.step_order
              ? {
                  ...s,
                  status: "running" as TaskStepStatus,
                  output: "",
                  tokensUsed: 0,
                  errorMessage: undefined,
                  isDelegated: data.is_delegated,
                  dependsOn: data.depends_on,
                }
              : s,
          );
        }
        return [
          ...base,
          {
            stepOrder: data.step_order,
            title: data.title,
            description: data.description,
            stepType: data.step_type,
            status: "running" as TaskStepStatus,
            output: "",
            tokensUsed: 0,
            isDelegated: data.is_delegated,
            dependsOn: data.depends_on,
          },
        ];
      });
      setExpandedSteps((prev) => new Set(prev).add(data.step_order));
    },
    [],
  );

  const handleStepContent = useCallback((data: { step_order: number; content: string }) => {
    const pending = pendingChunksRef.current;
    pending.set(data.step_order, (pending.get(data.step_order) || "") + data.content);
    const now = Date.now();
    if (now - lastStepFlushRef.current >= STEP_CONTENT_THROTTLE_MS) {
      const snapshot = new Map(pending);
      pending.clear();
      lastStepFlushRef.current = now;
      setStreamSteps((prev) =>
        prev.map((s) => {
          const extra = snapshot.get(s.stepOrder);
          return extra ? { ...s, output: s.output + extra } : s;
        }),
      );
    }
  }, []);

  const handleStepEnd = useCallback(
    (data: {
      step_order: number;
      title: string;
      output: string;
      summary?: string;
      tokens_used: number;
      status: string;
      sources?: SourceMetadata[];
      delegated_task_uuid?: string;
    }) => {
      pendingChunksRef.current.delete(data.step_order);
      setStreamSteps((prev) =>
        prev.map((s) =>
          s.stepOrder === data.step_order
            ? {
                ...s,
                status: (data.status as TaskStepStatus) || "completed",
                output: data.output || s.output,
                summary: data.summary,
                tokensUsed: data.tokens_used,
                sources: data.sources,
                delegatedTaskUuid: data.delegated_task_uuid || s.delegatedTaskUuid,
              }
            : s,
        ),
      );
      setTotalTokens((prev) => prev + data.tokens_used);
      setExpandedSteps((prev) => {
        const next = new Set(prev);
        next.delete(data.step_order);
        return next;
      });
    },
    [],
  );

  // ── Main event handler for operation stream ─────────────────────────
  const handleTaskEvent = useCallback(
    (event: OperationEvent) => {
      const { event: eventName, data } = event;

      switch (eventName) {
        case "task_start": {
          setLiveStatus("planning");
          break;
        }

        case "planning_start": {
          setLiveStatus("planning");
          break;
        }

        case "title_generated": {
          const d = data as { title: string };
          setTask((prev) => (prev ? { ...prev, title: d.title } : prev));
          break;
        }

        case "planning_end": {
          const d = data as {
            step_count: number;
            steps: PlanStep[];
            is_continuation?: boolean;
            continuation_start_order?: number;
          };
          if (d.is_continuation && d.continuation_start_order != null) {
            setContinuationStartOrder(d.continuation_start_order);
            setPlanSteps((prev) => [...prev, ...d.steps]);
          } else {
            setPlanSteps(d.steps);
          }
          setLiveStatus("running");
          break;
        }

        case "plan_revised": {
          const d = data as {
            message: string;
            new_steps: PlanStep[];
            revision_tokens: number;
          };
          const currentStreamSteps = streamStepsRef.current;
          setPlanSteps((prev) => {
            const completedOrders = new Set<number>();
            for (const ss of currentStreamSteps) {
              if (ss.status === "completed" || ss.status === "running") {
                completedOrders.add(ss.stepOrder);
              }
            }
            const kept = prev.filter((_, idx) => completedOrders.has(idx));
            return [...kept, ...d.new_steps];
          });
          setStreamSteps((prev) =>
            prev.filter((s) => s.status === "completed" || s.status === "running" || s.status === "failed"),
          );
          break;
        }

        case "step_start":
          handleStepStart(data as Parameters<typeof handleStepStart>[0]);
          break;

        case "step_content":
          handleStepContent(data as Parameters<typeof handleStepContent>[0]);
          break;

        case "step_end":
          handleStepEnd(data as Parameters<typeof handleStepEnd>[0]);
          break;

        case "step_failed": {
          const d = data as { step_order: number; error: string };
          setStreamSteps((prev) =>
            prev.map((s) =>
              s.stepOrder === d.step_order ? { ...s, status: "failed" as TaskStepStatus, errorMessage: d.error } : s,
            ),
          );
          break;
        }

        case "task_waiting": {
          const d = data as {
            message: string;
            options?: string[];
            questions?: AskUserQuestion[];
          };
          setLiveStatus("waiting_for_input");
          setAskUserQuestion(d.message);
          setAskUserOptions(d.options || null);
          setAskUserQuestions(d.questions || null);
          break;
        }

        case "task_resumed":
          setLiveStatus("running");
          break;

        case "synthesis_start":
          setSynthesizing(true);
          break;

        case "artifact_created":
          loadTask();
          break;

        case "task_end": {
          const d = data as { output: string; total_tokens: number };
          setSynthesizing(false);
          setFinalOutput(d.output);
          setTotalTokens(d.total_tokens);
          setLiveStatus("completed");
          setExecuting(false);
          setContinuationStartOrder(null);
          loadTask();
          break;
        }

        case "task_failed": {
          const d = data as { error: string; total_tokens?: number };
          setError(d.error);
          setLiveStatus("failed");
          setExecuting(false);
          if (d.total_tokens) setTotalTokens(d.total_tokens);
          loadTask();
          break;
        }

        case "task_cancelled": {
          const d = data as { message: string; total_tokens?: number };
          setLiveStatus("cancelled");
          setExecuting(false);
          if (d.total_tokens) setTotalTokens(d.total_tokens);
          loadTask();
          break;
        }

        case "operation_completed": {
          if (executing) {
            setExecuting(false);
            loadTask();
          }
          break;
        }

        case "operation_failed": {
          const d = data as { error?: string };
          if (executing) {
            setError(d.error || "Operation failed");
            setLiveStatus("failed");
            setExecuting(false);
            loadTask();
          }
          break;
        }

        case "error": {
          const d = data as { message: string };
          setError(d.message);
          setLiveStatus("failed");
          setExecuting(false);
          break;
        }
      }
    },
    [executing, handleStepStart, handleStepContent, handleStepEnd],
  );

  const { abort: abortStream } = useOperationStream(operationId, {
    onEvent: handleTaskEvent,
    onError: (err) => {
      setError(err);
      setLiveStatus("failed");
      setExecuting(false);
    },
  });

  // Reconnect to an active operation if one exists for this task
  // (e.g. user navigated away and came back while task is still running)
  useEffect(() => {
    if (!taskId || operationId || executing) return;
    const activeOp = findByEntityId(taskId);
    if (activeOp) {
      // Pre-populate streamSteps from loaded task steps so that incoming
      // SSE events (step_start, step_content, etc.) merge with rather
      // than replace the already-completed steps.
      if (task?.steps && task.steps.length > 0 && streamStepsRef.current.length === 0) {
        const existingSteps: StreamStep[] = task.steps.map((s) => ({
          stepOrder: s.stepOrder,
          title: s.title,
          description: s.description,
          stepType: s.stepType,
          status: s.status as TaskStepStatus,
          output: typeof s.output === "string" ? s.output : "",
          summary: s.summary,
          tokensUsed: s.tokensUsed,
          errorMessage: s.errorMessage,
          sources: s.sources,
          isDelegated: !!s.delegatedTaskUuid,
          delegatedTaskUuid: s.delegatedTaskUuid,
          dependsOn: s.dependsOn,
        }));
        setStreamSteps(existingSteps);
      }
      setOperationId(activeOp.operation_id);
      setExecuting(true);
      setLiveStatus("running");
    }
  }, [taskId, findByEntityId, operationId, executing]);

  const handleExecute = async () => {
    if (!taskId) return;

    setExecuting(true);
    setLiveStatus("planning");
    setPlanSteps([]);
    setStreamSteps([]);
    setFinalOutput(null);
    setTotalTokens(0);
    setError(null);
    setExpandedSteps(new Set());
    setSynthesizing(false);
    setFollowUpMessages([]);
    setFollowUpStreamingContent("");
    setAskUserQuestion(null);
    setAskUserOptions(null);
    setAskUserQuestions(null);
    setContinuationStartOrder(null);
    setOperationId(null);

    try {
      const result = await taskService.executeStream(taskId);
      setOperationId(result.operation_id);
      trackOperation({
        operation_id: result.operation_id,
        operation_type: "task_execution",
        entity_id: taskId,
        status: "running",
        title: task?.title || "Task execution",
        started_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        user_id: 0,
      });
    } catch (err) {
      console.error("Error executing task:", err);
      setError(err instanceof Error ? err.message : "Failed to execute task");
      setLiveStatus("failed");
      setExecuting(false);
    }
  };

  const handleCancel = async () => {
    if (!taskId) return;
    try {
      setCancelling(true);
      abortStream(); // Disconnect from the operation stream
      await taskService.cancel(taskId, { reason: "Cancelled by user" });
      setOperationId(null);
      setLiveStatus("cancelled");
      setExecuting(false);
      await loadTask();
    } catch (err) {
      console.error("Error cancelling task:", err);
      showToast("Failed to cancel task", "error");
    } finally {
      setCancelling(false);
    }
  };

  const submitInput = async (payload: { input?: string; responses?: import("@/types").TaskUserInputResponse[] }) => {
    if (!taskId) return;
    try {
      setSubmittingInput(true);
      setError(null);
      setAskUserQuestion(null);
      setAskUserOptions(null);
      setAskUserQuestions(null);
      setLiveStatus("running");
      setExecuting(true);
      setOperationId(null);

      // Pre-populate streamSteps from existing task steps so that when the
      // resumed step's step_start event arrives it's appended to (rather
      // than replacing) the completed steps.  Without this, displaySteps
      // switches from task.steps to streamSteps once the first SSE event
      // lands, hiding all previously completed steps.
      const existingSteps: StreamStep[] = (task?.steps || []).map((s) => ({
        stepOrder: s.stepOrder,
        title: s.title,
        description: s.description,
        stepType: s.stepType,
        status: s.status as TaskStepStatus,
        output: typeof s.output === "string" ? s.output : "",
        summary: s.summary,
        tokensUsed: s.tokensUsed,
        errorMessage: s.errorMessage,
        sources: s.sources,
        isDelegated: !!s.delegatedTaskUuid,
        delegatedTaskUuid: s.delegatedTaskUuid,
        dependsOn: s.dependsOn,
      }));
      setStreamSteps(existingSteps);

      const result = await taskService.provideInputStream(taskId, payload);
      setOperationId(result.operation_id);
      trackOperation({
        operation_id: result.operation_id,
        operation_type: "task_execution",
        entity_id: taskId,
        status: "running",
        title: task?.title || "Task execution",
        started_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        user_id: 0,
      });
    } catch (err) {
      console.error("Error providing input:", err);
      setError("Failed to submit input and resume task");
      setLiveStatus("failed");
      setExecuting(false);
    } finally {
      setSubmittingInput(false);
    }
  };

  /** Legacy handler — plain text input. */
  const handleProvideInputLegacy = (text: string) => {
    if (!text.trim()) return;
    submitInput({ input: text.trim() });
  };

  /** Structured handler — enhanced multi-question responses. */
  const handleProvideInputStructured = (responses: import("@/types").TaskUserInputResponse[]) => {
    submitInput({ responses });
  };

  const toggleStep = (stepOrder: number) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepOrder)) {
        next.delete(stepOrder);
      } else {
        next.add(stepOrder);
      }
      return next;
    });
  };

  /** Start a Level 2 continuation cycle from a follow-up message. */
  const startContinuation = async (goal: string) => {
    if (!taskId) return;

    // Pre-populate streamSteps from existing task steps so continuation
    // steps (with higher step_order values) append below them.
    const existingSteps: StreamStep[] = (task?.steps || []).map((s) => ({
      stepOrder: s.stepOrder,
      title: s.title,
      description: s.description,
      stepType: s.stepType,
      status: s.status as TaskStepStatus,
      output: typeof s.output === "string" ? s.output : "",
      summary: s.summary,
      tokensUsed: s.tokensUsed,
      errorMessage: s.errorMessage,
      sources: s.sources,
      isDelegated: !!s.delegatedTaskUuid,
      delegatedTaskUuid: s.delegatedTaskUuid,
      dependsOn: s.dependsOn,
    }));
    setStreamSteps(existingSteps);

    // Clear follow-up streaming state since we're switching to operation mode
    setFollowUpStreaming(false);
    setFollowUpStreamingContent("");
    setFollowUpActiveTool(null);

    // Transition to executing / planning state
    setExecuting(true);
    setLiveStatus("planning");
    setFinalOutput(null);
    setError(null);
    setSynthesizing(false);
    setAskUserQuestion(null);
    setAskUserOptions(null);
    setAskUserQuestions(null);
    setOperationId(null);

    try {
      const result = await taskService.continueStream(taskId, goal);
      setOperationId(result.operation_id);
      trackOperation({
        operation_id: result.operation_id,
        operation_type: "task_execution",
        entity_id: taskId,
        status: "running",
        title: task?.title || "Task continuation",
        started_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        user_id: 0,
      });
    } catch (err) {
      console.error("Error starting continuation:", err);
      setError(err instanceof Error ? err.message : "Failed to start task continuation");
      setLiveStatus("failed");
      setExecuting(false);
    }
  };

  /** Process a single SSE event during follow-up streaming.
   *  Returns `true` when the caller should exit the for-await loop
   *  (e.g. followup_continue hands off to continuation mode). */
  const processFollowUpEvent = async (
    // NOSONAR -- SSE event switch handler with per-event-type processing
    event: SSEEvent,
    contentSoFar: { value: string },
  ): Promise<boolean> => {
    switch (event.event) {
      case "followup_continue": {
        const data = event.data as { goal: string; message: string };
        setFollowUpMessages((prev) => [...prev, { role: "assistant", content: data.message }]);
        setFollowUpStreamingContent("");
        setFollowUpActiveTool(null);
        await startContinuation(data.goal);
        return true; // exit loop — operation stream takes over
      }
      case "followup_tool_start": {
        const payload = event.data as ToolStartEvent;
        setFollowUpActiveTool(payload.name);
        return false;
      }
      case "followup_tool_end": {
        setFollowUpActiveTool(null);
        const payload = event.data as ToolEndEvent;
        if (payload.metadata?.sources) {
          for (const src of payload.metadata.sources) {
            followUpSourcesRef.current.push(src);
          }
        }
        return false;
      }
      case "followup_delta": {
        const data = event.data as { delta: string };
        contentSoFar.value += data.delta;
        setFollowUpStreamingContent(contentSoFar.value);
        return false;
      }
      case "followup_end": {
        const data = event.data as { content: string };
        const finalContent = data.content || contentSoFar.value;
        const sources = followUpSourcesRef.current.length > 0 ? [...followUpSourcesRef.current] : undefined;
        setFollowUpMessages((prev) => [...prev, { role: "assistant", content: finalContent, sources }]);
        setFollowUpStreamingContent("");
        setFollowUpActiveTool(null);
        return false;
      }
      case "error": {
        const data = event.data as { message: string };
        setFollowUpMessages((prev) => [...prev, { role: "assistant", content: `Error: ${data.message}` }]);
        setFollowUpStreamingContent("");
        setFollowUpActiveTool(null);
        return false;
      }
      default:
        return false;
    }
  };

  const handleFollowUp = async () => {
    if (!taskId || !followUpInput.trim() || followUpStreaming) return;

    const message = followUpInput.trim();
    setFollowUpInput("");
    setFollowUpMessages((prev) => [...prev, { role: "user", content: message }]);
    setFollowUpStreaming(true);
    setFollowUpStreamingContent("");
    setFollowUpActiveTool(null);
    followUpSourcesRef.current = [];
    let switchedToContinuation = false;

    try {
      const stream = taskService.followUpStream(taskId, message);
      const contentSoFar = { value: "" };

      for await (const event of stream as AsyncGenerator<SSEEvent>) {
        const shouldExit = await processFollowUpEvent(event, contentSoFar);
        if (shouldExit) {
          switchedToContinuation = true;
          return;
        }
      }
    } catch (err) {
      console.error("Follow-up stream failed:", err);
      setFollowUpMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, an error occurred. Please try again.",
        },
      ]);
      setFollowUpStreamingContent("");
      setFollowUpActiveTool(null);
    } finally {
      if (!switchedToContinuation) {
        setFollowUpStreaming(false);
      }
    }
  };

  const toggleArtifact = (uuid: string) => {
    setExpandedArtifacts((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  };

  const handleCopyArtifact = async (artifact: TaskArtifact) => {
    if (!artifact.content) return;
    try {
      await navigator.clipboard.writeText(artifact.content);
      setCopiedArtifact(artifact.uuid);
      setTimeout(() => setCopiedArtifact(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleDownloadArtifact = (artifact: TaskArtifact) => {
    if (!artifact.content) return;
    const getExtension = (mimeType: string | undefined) => {
      if (mimeType === "application/json") return ".json";
      if (mimeType === "text/csv") return ".csv";
      if (mimeType === "text/plain") return ".txt";
      return ".md";
    };
    const ext = getExtension(artifact.mimeType);
    const blob = new Blob([artifact.content], { type: artifact.mimeType || "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${artifact.title.replace(/[^a-zA-Z0-9-_ ]/g, "").replace(/\s+/g, "_")}${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Derive the current status to show
  const displayStatus: TaskStatus = liveStatus || task?.status || "pending";
  const isRunning = executing || displayStatus === "running" || displayStatus === "planning";
  const canExecute =
    !executing &&
    (displayStatus === "pending" ||
      displayStatus === "failed" ||
      displayStatus === "cancelled" ||
      displayStatus === "completed");
  const showInput = displayStatus === "waiting_for_input";
  const showFollowUp =
    !executing &&
    (displayStatus === "completed" ||
      displayStatus === "failed" ||
      (!executing && (task?.status === "completed" || task?.status === "failed")));

  // Steps to render: prefer live stream steps, fall back to task.steps
  const displaySteps: (StreamStep | TaskStep)[] = streamSteps.length > 0 ? streamSteps : task?.steps || [];

  // -- render --

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (error && !task && !executing) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
        <h3 className="text-lg font-medium text-content-primary">{error}</h3>
        <button
          onClick={() => navigate("/tasks")}
          className="mt-4 px-4 py-2 bg-surface-secondary text-content-primary rounded-lg hover:bg-interactive-active transition-colors"
        >
          Back to Tasks
        </button>
      </div>
    );
  }

  if (!task && !executing) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <AlertCircle className="h-12 w-12 text-content-tertiary mb-4" />
        <h3 className="text-lg font-medium text-content-primary">Task not found</h3>
        <button
          onClick={() => navigate("/tasks")}
          className="mt-4 px-4 py-2 bg-surface-secondary text-content-primary rounded-lg hover:bg-interactive-active transition-colors"
        >
          Back to Tasks
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="shrink-0 p-6 border-b border-border-primary">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/tasks")}
              className="p-2 text-content-tertiary hover:text-content-primary hover:bg-interactive-hover rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold text-content-primary">{task?.title || "Task"}</h1>
                {(() => {
                  const cfg = statusConfig[displayStatus];
                  const StatusIcon = cfg.icon;
                  return (
                    <span
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}
                    >
                      <StatusIcon className={`h-3 w-3${displayStatus === "running" ? " animate-spin" : ""}`} />
                      {cfg.label}
                    </span>
                  );
                })()}
              </div>
              {task?.agentName && (
                <div className="flex items-center gap-1.5 mt-1 text-content-tertiary text-sm">
                  <Bot className="h-3.5 w-3.5" />
                  {task.agentName}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {canExecute && (
              <button
                onClick={handleExecute}
                className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                <Play className="h-4 w-4" />
                {displayStatus === "pending" ? "Execute" : "Re-run"}
              </button>
            )}
            {isRunning && (
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg font-medium hover:bg-red-500/30 transition-colors disabled:opacity-50"
              >
                {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />}
                Cancel
              </button>
            )}
            {task && <TagPopover entityType="task" entityId={task.uuid} mode="compact" />}
            {task && (
              <button
                onClick={() => setIsShareDialogOpen(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-surface-secondary hover:bg-interactive-active border border-border-secondary text-content-tertiary hover:text-content-primary rounded-lg transition text-sm"
                title="Share this task"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Goal */}
          <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-lg bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20 flex items-center justify-center">
                <ListTodo className="h-5 w-5 text-brand-primary" />
              </div>
              <h2 className="font-medium text-content-primary">Goal</h2>
            </div>
            <p className="text-content-secondary">{task?.goal || ""}</p>
            {task && (
              <div className="mt-4 flex items-center gap-6 text-sm text-content-quaternary">
                <span>
                  Created: <span className="text-content-tertiary">{new Date(task.createdAt).toLocaleString()}</span>
                </span>
                {task.totalTokens > 0 && (
                  <span>
                    Tokens: <span className="text-content-tertiary">{task.totalTokens.toLocaleString()}</span>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Planning Phase */}
          {displayStatus === "planning" && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 text-blue-400 animate-spin" />
                <div>
                  <h3 className="font-medium text-content-primary">Planning execution steps...</h3>
                  <p className="text-sm text-content-tertiary mt-1">
                    The agent is analyzing your goal and creating a step-by-step plan.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Plan Preview (shown after planning, before/during execution) */}
          {planSteps.length > 0 &&
            displayStatus !== "planning" &&
            (() => {
              // Build waves for plan steps to show parallel grouping
              const planWithOrders = planSteps.map((ps, idx) => ({
                ...ps,
                stepOrder: idx,
                dependsOn: ps.depends_on,
              }));
              const planWaves = buildWaves(planWithOrders);

              return (
                <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
                  <h2 className="font-medium text-content-primary mb-3">Execution Plan ({planSteps.length} steps)</h2>
                  <div className="space-y-2">
                    {planWaves.map((wave) => {
                      // Check if this wave starts a continuation section
                      const minPlanOrder = Math.min(...wave.steps.map((s) => s.stepOrder));
                      const planContinuationBoundary =
                        continuationStartOrder != null && minPlanOrder === continuationStartOrder;

                      const planSeparator = planContinuationBoundary ? (
                        <div key={`plan-cont-sep-${minPlanOrder}`} className="flex items-center gap-3 py-1.5">
                          <div className="flex-1 border-t border-brand-secondary/30" />
                          <span className="text-[10px] font-medium uppercase tracking-wider text-brand-secondary flex items-center gap-1.5">
                            <Play className="h-3 w-3" />
                            Continuation
                          </span>
                          <div className="flex-1 border-t border-brand-secondary/30" />
                        </div>
                      ) : null;

                      const renderPlanItem = (ps: (typeof planWithOrders)[number]) => {
                        const stepOrder = ps.stepOrder;
                        const matchingStep = streamSteps.find(
                          (s) => s.stepOrder === stepOrder, // NOSONAR — deeply nested React pattern
                        );
                        const isDone = matchingStep?.status === "completed";
                        const isActive = matchingStep?.status === "running";
                        const isFailed = matchingStep?.status === "failed";

                        const planItemStyle = (() => {
                          // NOSONAR — IIFE for inline style computation based on step status
                          if (isActive) return "bg-blue-500/10 border-blue-500/30";
                          if (isDone) return "bg-green-500/10 border-green-500/30";
                          if (isFailed) return "bg-red-500/10 border-red-500/30";
                          return "bg-surface-secondary/50 border-border-secondary";
                        })();

                        return (
                          <div
                            key={stepOrder}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${planItemStyle}`}
                          >
                            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-surface-secondary text-xs font-medium text-content-tertiary">
                              {stepOrder + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-sm text-content-primary">{ps.title}</span>
                              {ps.description && (
                                <span className="text-xs text-content-tertiary ml-2">- {ps.description}</span>
                              )}
                            </div>
                            {ps.is_delegated && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-purple-500/15 text-purple-400 border border-purple-500/20 shrink-0">
                                <GitBranch className="h-2.5 w-2.5" />
                                Delegated
                              </span>
                            )}
                            {isActive && <Loader2 className="h-3.5 w-3.5 text-blue-400 animate-spin shrink-0" />}
                            {isDone && <CheckCircle2 className="h-3.5 w-3.5 text-green-400 shrink-0" />}
                            {isFailed && <XCircle className="h-3.5 w-3.5 text-red-400 shrink-0" />}
                          </div>
                        );
                      };

                      if (!wave.isParallel) {
                        return (
                          <div key={`wave-${Math.min(...wave.steps.map((s) => s.stepOrder))}`}>
                            {planSeparator}
                            {wave.steps.map(renderPlanItem)}
                          </div>
                        );
                      }

                      return (
                        <div key={`wave-par-${Math.min(...wave.steps.map((s) => s.stepOrder))}`}>
                          {planSeparator}
                          <div className="relative pl-4 border-l-2 border-cyan-500/30">
                            <div className="absolute -left-px top-0 flex items-center gap-1 -translate-x-full pr-2">
                              <Layers className="h-3 w-3 text-cyan-400" />
                            </div>
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <span className="text-[10px] font-medium text-cyan-400 uppercase tracking-wider">
                                Parallel
                              </span>
                            </div>
                            <div className="space-y-2">{wave.steps.map(renderPlanItem)}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

          {/* Step Details (live streaming or from loaded task) */}
          {displaySteps.length > 0 &&
            (() => {
              // Build waves for step grouping
              const stepWaves = buildWaves(
                displaySteps.map((s) => ({
                  ...s,
                  stepOrder: s.stepOrder,
                  dependsOn: "dependsOn" in s ? ((s as StreamStep).dependsOn ?? (s as TaskStep).dependsOn) : [],
                })),
              );

              const renderStepCard = (step: (typeof displaySteps)[number]) => {
                const order = step.stepOrder;
                const title = step.title;
                const description = step.description;
                const status = step.status;
                const output = typeof step.output === "string" ? step.output : "";
                const errorMsg = step.errorMessage;
                const tokens = step.tokensUsed;
                const isExpanded = expandedSteps.has(order);
                // Delegation info
                const isDelegated = "isDelegated" in step ? step.isDelegated : false;
                const delegatedTaskUuid =
                  "delegatedTaskUuid" in step ? (step as TaskStep).delegatedTaskUuid : undefined;
                // Find child summary from parent task's children array
                const childSummary = task?.children?.find((c: ChildTaskSummary) => c.parentStepOrder === order);
                const hasExpandableContent = !!(output || errorMsg || description || isDelegated);

                return (
                  <div key={order} className={`rounded-lg border transition-colors ${stepBorderClass(status)}`}>
                    <button
                      onClick={() => hasExpandableContent && toggleStep(order)}
                      className={`w-full flex items-center gap-4 p-4 text-left${hasExpandableContent ? " cursor-pointer" : " cursor-default"}`}
                    >
                      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-surface-secondary text-sm font-medium text-content-tertiary shrink-0">
                        {order + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-content-primary">{title}</p>
                          {isDelegated && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-purple-500/15 text-purple-400 border border-purple-500/20">
                              <GitBranch className="h-2.5 w-2.5" />
                              Delegated
                            </span>
                          )}
                        </div>
                        {tokens > 0 && (
                          <p className="text-xs text-content-tertiary">{tokens.toLocaleString()} tokens</p>
                        )}
                      </div>
                      <StepStatusIcon status={status} />
                      {hasExpandableContent && isExpanded && (
                        <ChevronDown className="h-4 w-4 text-content-tertiary shrink-0" />
                      )}
                      {hasExpandableContent && !isExpanded && (
                        <ChevronRight className="h-4 w-4 text-content-tertiary shrink-0" />
                      )}
                    </button>
                    {isExpanded && hasExpandableContent && (
                      <div className="px-4 pb-4 pt-0">
                        {errorMsg && (
                          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg mb-2">
                            <p className="text-sm text-red-300">{errorMsg}</p>
                          </div>
                        )}
                        {output &&
                          (() => {
                            const summary =
                              "summary" in step ? (step as StreamStep).summary : (step as TaskStep).summary;
                            const hasSummary = !!summary && summary !== output;
                            const showFull = fullOutputSteps.has(order);
                            const displayContent = hasSummary && !showFull ? summary : output;
                            return (
                              <>
                                {hasSummary && (
                                  <div className="flex items-center justify-end mb-1.5">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setFullOutputSteps((prev) => {
                                          // NOSONAR — state updater toggling Set membership; complexity inherent to toggle pattern
                                          const next = new Set(prev);
                                          if (next.has(order)) next.delete(order);
                                          else next.add(order);
                                          return next;
                                        });
                                      }}
                                      className="flex items-center gap-1.5 text-xs text-content-tertiary hover:text-content-secondary transition-colors px-2 py-1 rounded-sm hover:bg-surface-tertiary/30"
                                    >
                                      {showFull ? (
                                        <>
                                          <Minimize2 className="h-3 w-3" />
                                          Show Summary
                                        </>
                                      ) : (
                                        <>
                                          <Maximize2 className="h-3 w-3" />
                                          Show Full Output
                                        </>
                                      )}
                                    </button>
                                  </div>
                                )}
                                <div className="p-3 bg-surface-secondary rounded-lg max-h-80 overflow-auto">
                                  <Markdown content={displayContent} isStreaming={status === "running"} />
                                </div>
                              </>
                            );
                          })()}
                        {description && !output && !errorMsg && (
                          <div className="p-3 bg-surface-secondary rounded-lg">
                            <p className="text-sm text-content-secondary">{description}</p>
                          </div>
                        )}
                        {step.sources && step.sources.length > 0 && !isDelegated && (
                          <SourcesPanel sources={step.sources} />
                        )}
                        {isDelegated && delegatedTaskUuid && (
                          <DelegatedStepDetail
                            childTaskUuid={delegatedTaskUuid}
                            childSummary={childSummary}
                            parentStepOrder={order}
                            parentStepSources={step.sources}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              };

              // Compute continuation start orders for visual separator
              const continuationStarts = new Set<number>();
              if (continuationStartOrder != null) {
                // Live streaming — we know the exact start order
                continuationStarts.add(continuationStartOrder);
              } else if (task?.context?.continuation_history) {
                // Loaded from DB — extract start orders from history
                const history = task.context.continuation_history as Array<{
                  start_step_order?: number;
                }>;
                for (const entry of history) {
                  if (entry.start_step_order != null) {
                    continuationStarts.add(entry.start_step_order);
                  }
                }
              }

              return (
                <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
                  <h2 className="font-medium text-content-primary mb-4">Step Progress</h2>
                  <div className="space-y-3">
                    {stepWaves.map((wave) => {
                      // Check if this wave starts a continuation section
                      const minStepOrder = Math.min(...wave.steps.map((s) => s.stepOrder));
                      const isContinuationBoundary = continuationStarts.has(minStepOrder);

                      const separator = isContinuationBoundary ? (
                        <div key={`cont-sep-${minStepOrder}`} className="flex items-center gap-3 py-2">
                          <div className="flex-1 border-t border-brand-secondary/30" />
                          <span className="text-[10px] font-medium uppercase tracking-wider text-brand-secondary flex items-center gap-1.5">
                            <Play className="h-3 w-3" />
                            Continuation
                          </span>
                          <div className="flex-1 border-t border-brand-secondary/30" />
                        </div>
                      ) : null;
                      if (!wave.isParallel) {
                        return (
                          <div key={`sw-${minStepOrder}`}>
                            {separator}
                            {wave.steps.map((s) => renderStepCard(s as (typeof displaySteps)[number]))}
                          </div>
                        );
                      }

                      // Parallel wave — visual grouping with left accent border
                      const allDone = wave.steps.every((s) => s.status === "completed");
                      const anyRunning = wave.steps.some((s) => s.status === "running");
                      const anyFailed = wave.steps.some((s) => s.status === "failed");
                      const getAccentColor = () => {
                        if (anyFailed) return "border-red-500/40";
                        if (anyRunning) return "border-cyan-500/40";
                        if (allDone) return "border-green-500/30";
                        return "border-border-secondary";
                      };
                      const getLabelColor = () => {
                        if (anyFailed) return "text-red-400";
                        if (anyRunning) return "text-cyan-400";
                        if (allDone) return "text-green-400";
                        return "text-content-quaternary";
                      };
                      const accentColor = getAccentColor();
                      const labelColor = getLabelColor();

                      return (
                        <div key={`sw-par-${minStepOrder}`}>
                          {separator}
                          <div className={`relative pl-4 border-l-2 ${accentColor}`}>
                            <div className="flex items-center gap-1.5 mb-2">
                              <Layers className={`h-3 w-3 ${labelColor}`} />
                              <span className={`text-[10px] font-medium uppercase tracking-wider ${labelColor}`}>
                                Parallel ({wave.steps.length} steps)
                              </span>
                              {anyRunning && <Loader2 className="h-3 w-3 text-cyan-400 animate-spin" />}
                            </div>
                            <div className="space-y-3">
                              {wave.steps.map((s) => renderStepCard(s as (typeof displaySteps)[number]))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

          {/* Synthesizing Indicator */}
          {synthesizing && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-6">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 text-purple-400 animate-spin" />
                <div>
                  <h3 className="font-medium text-content-primary">Synthesizing final output...</h3>
                  <p className="text-sm text-content-tertiary mt-1">
                    Combining all step findings into a cohesive answer.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Waiting for Input */}
          {showInput && (
            <AskUserCard
              question={askUserQuestion}
              options={askUserOptions}
              questions={askUserQuestions}
              submitting={submittingInput}
              onSubmitLegacy={handleProvideInputLegacy}
              onSubmitStructured={handleProvideInputStructured}
            />
          )}

          {/* Error banner (for failed state, not blocking) */}
          {error && displayStatus === "failed" && (task || executing) && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-400 shrink-0" />
                <div>
                  <h3 className="font-medium text-content-primary">Task Failed</h3>
                  <p className="text-sm text-red-300 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Final Output */}
          {(finalOutput || task?.finalOutput) &&
            (displayStatus === "completed" || (!executing && task?.status === "completed")) && (
              <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-green-400" />
                  </div>
                  <div>
                    <h2 className="font-medium text-content-primary">Final Output</h2>
                    <p className="text-sm text-content-tertiary">
                      {totalTokens > 0 || (task?.totalTokens ?? 0) > 0
                        ? `${(totalTokens || task?.totalTokens || 0).toLocaleString()} total tokens used`
                        : "Task completed successfully"}
                    </p>
                  </div>
                </div>
                <div className="p-4 bg-surface-secondary rounded-lg max-h-[600px] overflow-auto">
                  <Markdown content={finalOutput || task?.finalOutput || ""} />
                </div>
              </div>
            )}

          {/* Artifacts */}
          {task?.artifacts && task.artifacts.length > 0 && (
            <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-brand-secondary/20 flex items-center justify-center">
                  <FileOutput className="h-5 w-5 text-brand-secondary" />
                </div>
                <div>
                  <h2 className="font-medium text-content-primary">Artifacts ({task.artifacts.length})</h2>
                  <p className="text-sm text-content-tertiary">Deliverables produced during task execution</p>
                </div>
              </div>
              <div className="space-y-3">
                {task.artifacts.map((artifact) => {
                  const isExpanded = expandedArtifacts.has(artifact.uuid);
                  const isCopied = copiedArtifact === artifact.uuid;
                  return (
                    <div
                      key={artifact.uuid}
                      className="bg-surface-secondary rounded-lg border border-border-secondary overflow-hidden"
                    >
                      {/* Artifact header */}
                      <button
                        onClick={() => toggleArtifact(artifact.uuid)}
                        className="w-full flex items-center justify-between p-4 hover:bg-interactive-active transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <ArtifactTypeIcon type={artifact.artifactType} />
                          <div>
                            <h4 className="text-content-primary font-medium">{artifact.title}</h4>
                            <p className="text-xs text-content-tertiary mt-0.5">
                              {artifactTypeLabels[artifact.artifactType] || artifact.artifactType} ·{" "}
                              {new Date(artifact.createdAt).toLocaleString()}
                              {artifact.content && (
                                <span className="ml-2 text-content-quaternary">
                                  ({(artifact.content.length / 1024).toFixed(1)} KB)
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {artifact.content && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyArtifact(artifact);
                                }}
                                className="p-1.5 rounded-md hover:bg-interactive-active transition-colors"
                                title="Copy content"
                              >
                                {isCopied ? (
                                  <Check className="h-4 w-4 text-green-400" />
                                ) : (
                                  <Copy className="h-4 w-4 text-content-tertiary" />
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadArtifact(artifact);
                                }}
                                className="p-1.5 rounded-md hover:bg-interactive-active transition-colors"
                                title="Download artifact"
                              >
                                <Download className="h-4 w-4 text-content-tertiary" />
                              </button>
                            </>
                          )}
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-content-tertiary" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-content-tertiary" />
                          )}
                        </div>
                      </button>
                      {/* Artifact content */}
                      {isExpanded && artifact.content && (
                        <div className="border-t border-border-secondary p-4 max-h-[500px] overflow-auto">
                          <Markdown content={artifact.content} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Follow-up Conversation Thread */}
          {showFollowUp && followUpMessages.length > 0 && (
            <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-brand-secondary/20 flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-brand-secondary" />
                </div>
                <h2 className="font-medium text-content-primary">Follow-up Conversation</h2>
              </div>
              <div className="space-y-4">
                {followUpMessages.map((msg, idx) => (
                  <div key={`followup-${msg.role}-${idx}`} className="flex gap-3">
                    <div
                      className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                        msg.role === "user" ? "bg-linear-to-br from-slate-600 to-slate-700" : "bg-brand-gradient-br"
                      }`}
                    >
                      {msg.role === "user" ? (
                        <User className="w-4 h-4 text-content-primary" />
                      ) : (
                        <Bot className="w-4 h-4 text-content-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium text-content-tertiary block mb-1">
                        {msg.role === "user" ? "You" : "AI Assistant"}
                      </span>
                      {msg.role === "assistant" ? (
                        <div className="text-content-secondary">
                          <Markdown content={msg.content} />
                        </div>
                      ) : (
                        <p className="text-content-secondary whitespace-pre-wrap">{msg.content}</p>
                      )}
                      {/* Sources for this assistant message */}
                      {msg.role === "assistant" && msg.sources && msg.sources.length > 0 && (
                        <SourcesPanel sources={msg.sources} />
                      )}
                    </div>
                  </div>
                ))}
                {/* Tool activity indicator during follow-up streaming */}
                {followUpStreaming && followUpActiveTool && (
                  <div className="flex gap-3">
                    <div className="w-7 h-7 rounded-lg bg-brand-gradient-br flex items-center justify-center shrink-0">
                      {(() => {
                        const { icon: ToolIcon } = getFollowUpToolLabel(followUpActiveTool);
                        return <ToolIcon className="w-4 h-4 text-content-primary" />;
                      })()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-3.5 h-3.5 text-brand-primary animate-spin" />
                        <span className="text-xs text-content-tertiary">
                          {getFollowUpToolLabel(followUpActiveTool).label}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                {/* Streaming follow-up response */}
                {followUpStreaming && (
                  <div className="flex gap-3">
                    <div className="w-7 h-7 rounded-lg bg-brand-gradient-br flex items-center justify-center shrink-0">
                      <Bot className="w-4 h-4 text-content-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium text-content-tertiary block mb-1">AI Assistant</span>
                      {followUpStreamingContent ? (
                        <div className="relative text-content-secondary">
                          <Markdown content={followUpStreamingContent} isStreaming />
                          <span className="inline-block w-2 h-4 bg-brand-primary ml-1 animate-pulse" />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-brand-primary rounded-full animate-bounce" />
                          <div
                            className="w-2 h-2 bg-brand-primary rounded-full animate-bounce"
                            style={{ animationDelay: "0.2s" }}
                          />
                          <div
                            className="w-2 h-2 bg-brand-primary rounded-full animate-bounce"
                            style={{ animationDelay: "0.4s" }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      </div>

      {/* Follow-up Input Bar (fixed at bottom when task is completed) */}
      {showFollowUp && (
        <div className="shrink-0 border-t border-border-primary bg-surface-primary p-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-3">
              <VoiceInputButton
                onTranscript={(text) => setFollowUpInput((prev) => (prev ? `${prev} ${text}` : text))}
                disabled={followUpStreaming}
              />
              <textarea
                placeholder={
                  displayStatus === "failed"
                    ? "Ask a follow-up question or request a retry..."
                    : "Ask a follow-up question..."
                }
                value={followUpInput}
                onChange={(e) => setFollowUpInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleFollowUp();
                  }
                }}
                disabled={followUpStreaming}
                className="flex-1 bg-surface-secondary text-content-primary px-4 py-3 rounded-lg border border-border-secondary focus:outline-hidden focus:ring-2 focus:ring-brand-secondary/50 focus:border-brand-secondary placeholder-content-quaternary disabled:opacity-50 resize-none"
                rows={3}
              />
              <div className="shrink-0 self-end">
                <button
                  onClick={handleFollowUp}
                  disabled={followUpStreaming || !followUpInput.trim()}
                  className="flex items-center justify-center w-10 h-10 bg-brand-gradient rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {followUpStreaming ? (
                    <Loader2 className="h-4 w-4 text-content-primary animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 text-content-primary" />
                  )}
                </button>
              </div>
            </div>
            <div className="mt-2 text-xs text-content-quaternary">Press Enter to send, Shift+Enter for new line</div>
          </div>
        </div>
      )}

      {task && (
        <EntityShareDialog
          entityId={task.uuid}
          entityTitle={task.title || task.goal || "Untitled Task"}
          isOpen={isShareDialogOpen}
          onClose={() => setIsShareDialogOpen(false)}
          entityLabel="Task"
          entityType="task"
        />
      )}
    </div>
  );
}
